"use client";

import { Box, Button, Link, Paper, Stack, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useCallback, useEffect, useId, useState } from "react";

const STORAGE_KEY = "diningmate_gdpr_consent";

type ConsentStatus = "accepted" | "declined";

type StoredConsent = {
    status: ConsentStatus;
    timestamp: string;
};

const ConsentPaper = styled(Paper)(({ theme }) => ({
    width: "100%",
    maxWidth: 520,
    padding: theme.spacing(3),
    boxShadow: theme.shadows[8],
    borderRadius: 16,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
}));

const readStoredConsent = (): StoredConsent | null => {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) {
            return null;
        }
        const parsed = JSON.parse(raw);
        if (parsed && (parsed.status === "accepted" || parsed.status === "declined")) {
            return parsed as StoredConsent;
        }
    } catch (error) {
        // eslint-disable-next-line no-console
        console.warn("Unable to read GDPR consent from storage", error);
    }
    return null;
};

const storeConsent = (status: ConsentStatus) => {
    try {
        const payload: StoredConsent = {
            status,
            timestamp: new Date().toISOString(),
        };
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch (error) {
        // eslint-disable-next-line no-console
        console.warn("Unable to persist GDPR consent", error);
    }
};

const GdprConsent = (): JSX.Element | null => {
    const descriptionId = useId();
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        const consent = readStoredConsent();
        if (!consent) {
            setIsOpen(true);
        }
    }, []);

    const handleChoice = useCallback((status: ConsentStatus) => {
        storeConsent(status);
        setIsOpen(false);
    }, []);

    if (!isOpen) {
        return null;
    }

    return (
        <Box
            component="section"
            role="dialog"
            aria-modal="false"
            aria-describedby={descriptionId}
            sx={{
                position: "fixed",
                bottom: { xs: 16, md: 24 },
                left: { xs: 16, md: "auto" },
                right: 16,
                display: "flex",
                justifyContent: "center",
                zIndex: (theme) => theme.zIndex.snackbar,
                pointerEvents: "none",
            }}
        >
            <ConsentPaper elevation={0} sx={{ pointerEvents: "auto" }}>
                <Stack spacing={2}>
                    <Typography component="h2" variant="h6" sx={{ fontWeight: 700 }}>
                        We respect your privacy
                    </Typography>
                    <Typography id={descriptionId} variant="body2" color="textSecondary">
                        We use essential cookies to operate DiningMate and analytics to improve the
                        dining experience. Read our <Link href="/privacy">Privacy Policy</Link> and{" "}
                        <Link href="/terms">Terms of Service</Link>. Choose whether we may process
                        non-essential data.
                    </Typography>
                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1.5}
                        useFlexGap
                        alignItems="stretch"
                    >
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => handleChoice("accepted")}
                            sx={{ flex: 1 }}
                        >
                            Accept all
                        </Button>
                        <Button
                            variant="outlined"
                            color="inherit"
                            onClick={() => handleChoice("declined")}
                            sx={{ flex: 1 }}
                        >
                            Decline optional
                        </Button>
                    </Stack>
                </Stack>
            </ConsentPaper>
        </Box>
    );
};

export default GdprConsent;
